import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, Trash2, MapPin, GripVertical, Store, ChevronDown, ArrowUp, ArrowDown,
  CircleCheck, CircleAlert, StickyNote,
} from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { SearchableSelect } from '../ui/SearchableSelect';
import { Switch } from '../ui/Switch';
import { FieldLabel } from '../ui/FieldLabel';
import { Skeleton } from '../ui/Skeleton';
import { EmptyState } from '../ui/EmptyState';
import { PendingBackendNotice } from '../ui/PendingBackendNotice';
import { cn } from '../../utils/cn';
import { formatCLP } from '../../utils/formatters';
import { matchCommerce, toCommerceOption } from '../../utils/commerceSearch';
import { formatCommerceOptionLabel } from '../../views/adminView/cobro/components/commerceOptionLabel';
import { addRouteApi, updateRoute } from '../../api/routes';
import { routeErrorText } from '../../utils/routeApiErrors';
import { toast } from '../../utils/toast';

// Frecuencia en el formato legacy que entienden el back y la app del repartidor
// — EL MISMO que envía createRouteBeartrack/adminWithdrawals (el flujo que SÍ le
// aparece al chofer): 'weekday' | 'saturday' | 'sunday'. El back solo modela esos
// tres; las recurrencias más finas (diaria / multi-día) se aproximan a 'weekday'.
// Esto es lo que hace que la ruta le llegue al repartidor en /routes/me.
function recurrenciaToDayType(recurrenciaTipo, diasSemana) {
  if (recurrenciaTipo === 'semanal') {
    if (diasSemana.length === 1 && diasSemana[0] === 6) return 'saturday';
    if (diasSemana.length === 1 && diasSemana[0] === 0) return 'sunday';
  }
  return 'weekday';
}

// Mapea la recurrencia del drawer al contrato canónico del backend de rutas:
// recurrence:{type, daysOfWeek}. El back deriva `frequency` de aquí (pre-save hook)
// y la app de repartidores (GET /routes/me) matchea por recurrence.type/daysOfWeek,
// así que ESTE es el campo que hace que la ruta le aparezca al chofer el día correcto.
function buildBackendRecurrence(recurrenciaTipo, diasSemana) {
  if (recurrenciaTipo === 'diaria') {
    return { type: 'daily', daysOfWeek: [0, 1, 2, 3, 4, 5, 6] };
  }
  if (recurrenciaTipo === 'semanal') {
    const dows = [...new Set(diasSemana)].sort((a, b) => a - b);
    return { type: dows.length === 1 ? 'weekly' : 'custom', daysOfWeek: dows };
  }
  // 'laboral' y fallback
  return { type: 'weekdays', daysOfWeek: [1, 2, 3, 4, 5] };
}

// Mapea una parada del formulario al contrato de cliente del backend de rutas.
// CLAVE: el back persiste TODO el detalle operativo DENTRO de clients[] (no en un
// array `paradas` aparte) y con nombres en inglés: window{start,end}, expectedBulks,
// expectedOrders, expectedWeightKg, clientCharge, driverPayment, freeOrdersThreshold,
// geofencingEnabled + geofencingMeters, notes. `selectedAddress` lo normaliza el
// back a `shippingAddress`. Los vacíos se omiten para no guardar 0 espurios.
function buildClientFromParada(p) {
  const num = (v) =>
    v === '' || v === null || v === undefined ? undefined : Number(v);

  const client = {
    id: p.clientId,
    selectedAddress: p.selectedAddress || undefined,
    geofencingEnabled: Boolean(p.geofencingActivo),
  };

  // Ventana: ambos extremos en "HH:MM" (lo que ya guarda el state y exige el back).
  if (p.ventanaDesde && p.ventanaHasta) {
    client.window = { start: p.ventanaDesde, end: p.ventanaHasta };
  }

  const expectedBulks = num(p.bultosEsperados);
  if (expectedBulks !== undefined) client.expectedBulks = expectedBulks;

  const expectedOrders = num(p.pedidosEsperados);
  if (expectedOrders !== undefined) client.expectedOrders = expectedOrders;

  const expectedWeightKg = num(p.pesoEsperadoKg);
  if (expectedWeightKg !== undefined) client.expectedWeightKg = expectedWeightKg;

  const clientCharge = num(p.valorCobrarCliente);
  if (clientCharge !== undefined) client.clientCharge = clientCharge;

  const driverPayment = num(p.valorPagarRepartidor);
  if (driverPayment !== undefined) client.driverPayment = driverPayment;

  const freeOrdersThreshold = num(p.umbralCobroPedidos);
  if (freeOrdersThreshold !== undefined) client.freeOrdersThreshold = freeOrdersThreshold;

  // Guardamos el radio aunque el geofencing esté apagado, para no perder el valor
  // si el usuario lo reactiva luego.
  const geofencingMeters = num(p.geofencingMetros);
  if (geofencingMeters !== undefined) client.geofencingMeters = geofencingMeters;

  const notas = p.notas?.trim();
  if (notas) client.notes = notas;

  return client;
}

// Semana operativa de lunes a domingo (el valor sigue siendo el getDay() de JS).
const DIAS_SEMANA = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

const LABORAL_DIAS = [1, 2, 3, 4, 5];
const TODOS_DIAS = [0, 1, 2, 3, 4, 5, 6];

// 'puntual' (día único) deshabilitado por ahora: el backend de rutas no modela
// recurrencia de un solo día (recurrence solo expresa daily/weekdays/weekly/custom).
// Reactivar cuando exista soporte de eventos puntuales.
const RECURRENCIA_OPTS = [
  { value: 'laboral', label: 'Lunes a viernes' },
  { value: 'diaria', label: 'Todos los días' },
  { value: 'semanal', label: 'Personalizado' },
];

const MODALIDAD_OPTS = [
  { value: 'hub', label: 'A hub destino', hint: 'Los retiros se consolidan en un hub antes de salir a ruta.' },
  { value: 'pick_and_go', label: 'Pick & Go', hint: 'Lo retirado sale directo a ruta, sin pasar por hub.' },
];

// Días que efectivamente cubre la recurrencia (los chips siempre los reflejan).
function diasEfectivos(recurrenciaTipo, diasSemana) {
  if (recurrenciaTipo === 'laboral') return LABORAL_DIAS;
  if (recurrenciaTipo === 'diaria') return TODOS_DIAS;
  return diasSemana;
}

// Direcciones reales (con `address`) de un cliente del catálogo.
function clientAddresses(client) {
  return (client?.shippingAddresses || []).filter((a) => a && a.address);
}

// El nombre comercial es lo que el operador reconoce; la razón social queda como respaldo
// cuando el comercio no tiene nombre y, si existe, se ve aparte en la 2ª línea del menú.
// Se recortan: un nombre con solo espacios no puede ganarle a una razón social válida, o el
// rótulo quedaría en blanco y la validación de guardado rechazaría la parada.
function clientDisplayName(client) {
  return String(client?.name ?? '').trim() || String(client?.legalName ?? '').trim();
}

// react-select entrega `{ label, value, data }` y los campos del comercio quedan en `data`.
const filterCommerceOption = (option, query) => matchCommerce(option.data, query);

function emptyParada() {
  return {
    _tempId: Math.random().toString(36).slice(2),
    clientId: null,
    selectedAddress: '',
    comercioNombre: '',
    direccion: '',
    comuna: '',
    ventanaDesde: '09:00',
    ventanaHasta: '10:00',
    bultosEsperados: '',
    pedidosEsperados: '',
    pesoEsperadoKg: '',
    valorCobrarCliente: '',
    valorPagarRepartidor: '',
    umbralCobroPedidos: '',
    geofencingActivo: true,
    geofencingMetros: 100,
    notas: '',
  };
}

/**
 * Drawer para crear o editar una plantilla de ruta de recoleccion.
 *
 * Props:
 *   drivers        Array de conductores activos del backend.
 *   clients        Catálogo de clientes activos (con shippingAddresses). Alimenta
 *                  el autocompletado de dirección por parada.
 *   clientsLoading true mientras se cargan los clientes (muestra skeleton).
 *   hubs           Array de hubs disponibles (derivados del JWT).
 */
export function ProgramarRecoleccionModal({
  open,
  onClose,
  editandoRuta,
  onSaved,
  drivers = [],
  clients = [],
  clientsLoading = false,
  hubs = [],
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(editandoRuta);

  // Estado del formulario
  const [nombre, setNombre] = useState('');
  const [hubDestinoId, setHubDestinoId] = useState('');
  const [modalidad, setModalidad] = useState('hub');
  const [recurrenciaTipo, setRecurrenciaTipo] = useState('laboral');
  const [diasSemana, setDiasSemana] = useState([1, 2, 3, 4, 5]);
  const [fechaPuntual, setFechaPuntual] = useState('');
  const [conductorId, setConductorId] = useState('');
  const [notas, setNotas] = useState('');
  const [paradas, setParadas] = useState([emptyParada()]);
  const [saving, setSaving] = useState(false);
  // Índice de la parada que se está arrastrando (drag & drop nativo).
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  // Una sola parada con los detalles operativos abiertos a la vez.
  const [expandedId, setExpandedId] = useState(null);
  // Parada recién agregada: su buscador de comercio recibe el foco al montarse.
  const [focusId, setFocusId] = useState(null);
  const [notasOpen, setNotasOpen] = useState(false);

  // Snapshot del conjunto COMPLETO de paradas al abrir el drawer en edición, como
  // claves `clientId_selectedAddress`. Sirve para detectar en handleSave qué paradas
  // eliminó el usuario y pedir confirmación explícita antes de mandar confirmRemoval.
  const originalStopKeysRef = useRef(new Set());

  // Índice de clientes por id, para resolver nombre/direcciones en O(1).
  const clientsById = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(clients) ? clients : [];
    for (const c of list) m.set(c._id || c.id, c);
    return m;
  }, [clients]);

  // El contenido depende de los clientes (para autocompletar direcciones). En
  // edición esperamos a que lleguen antes de pintar las paradas: evita el
  // "salto" donde primero se ve texto plano y luego cambia a selector.
  const showSkeleton = clientsLoading && clients.length === 0;

  // Inicializar con datos de edicion
  useEffect(() => {
    if (open && editandoRuta) {
      setNombre(editandoRuta.nombre ?? '');
      setHubDestinoId(editandoRuta.hubDestinoId ?? '');
      setModalidad(editandoRuta.modalidadEntrega ?? 'hub');
      setRecurrenciaTipo(editandoRuta.recurrencia?.tipo ?? 'laboral');
      setDiasSemana(editandoRuta.recurrencia?.dias ?? [1, 2, 3, 4, 5]);
      setFechaPuntual(editandoRuta.recurrencia?.fecha ?? '');
      setConductorId(editandoRuta.conductorPorDefectoId ?? '');
      setNotas(editandoRuta.notas ?? '');
      setNotasOpen(Boolean(editandoRuta.notas));
      setExpandedId(null);
      setFocusId(null);
      // Snapshot del conjunto original COMPLETO (garantía de FE-A: editandoRuta.paradas
      // trae TODAS las paradas de la ruta). Identidad = clientId_selectedAddress.
      originalStopKeysRef.current = new Set(
        (editandoRuta.paradas ?? []).map(
          (p) => `${String(p.clientId)}_${String(p.selectedAddress ?? '')}`,
        ),
      );
      setParadas(
        (editandoRuta.paradas ?? []).map((p) => ({
          _tempId: p.id || Math.random().toString(36).slice(2),
          id: p.id,
          // IDs reales para guardar contra el back (clients:[{id, selectedAddress}]).
          clientId: p.clientId ?? null,
          selectedAddress: p.selectedAddress ?? '',
          comercioNombre: p.comercioNombre ?? '',
          direccion: p.direccion ?? '',
          comuna: p.comuna ?? '',
          ventanaDesde: p.ventanaDesde?.slice(11, 16) ?? '09:00',
          ventanaHasta: p.ventanaHasta?.slice(11, 16) ?? '10:00',
          bultosEsperados: p.bultosEsperados ?? '',
          pedidosEsperados: p.pedidosEsperados ?? '',
          pesoEsperadoKg: p.pesoEsperadoKg ?? '',
          valorCobrarCliente: p.valorCobrarCliente ?? '',
          valorPagarRepartidor: p.valorPagarRepartidor ?? '',
          umbralCobroPedidos: p.umbralCobroPedidos ?? '',
          geofencingActivo: p.geofencingActivo ?? true,
          geofencingMetros: p.geofencingMetros ?? 100,
          notas: p.notas ?? '',
        })),
      );
    } else if (open && !editandoRuta) {
      // Resetear para crear
      originalStopKeysRef.current = new Set();
      setNombre('');
      setHubDestinoId('');
      setModalidad('');
      setRecurrenciaTipo('laboral');
      setDiasSemana([1, 2, 3, 4, 5]);
      setFechaPuntual('');
      setConductorId('');
      setNotas('');
      setNotasOpen(false);
      setExpandedId(null);
      setFocusId(null);
      setParadas([emptyParada()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editandoRuta]);

  // Si la modalidad es "A hub destino" y solo hay 1 hub disponible, queda
  // preseleccionado (no tiene sentido pedir elegir cuando hay una sola opción).
  useEffect(() => {
    if (modalidad === 'hub' && hubs.length === 1 && !hubDestinoId) {
      setHubDestinoId(hubs[0].id);
    }
  }, [modalidad, hubs, hubDestinoId]);

  const diasActivos = diasEfectivos(recurrenciaTipo, diasSemana);

  // Tocar un día parte de los días efectivos del preset actual y pasa a
  // "Personalizado" (semanal), igual que antes al elegir "Días específicos".
  const toggleDia = (dia) => {
    const next = diasActivos.includes(dia)
      ? diasActivos.filter((d) => d !== dia)
      : [...diasActivos, dia].sort((a, b) => a - b);
    setDiasSemana(next);
    setRecurrenciaTipo('semanal');
  };

  const selectRecurrencia = (tipo) => {
    if (tipo === 'semanal') setDiasSemana(diasActivos);
    setRecurrenciaTipo(tipo);
  };

  const updateParada = (tempId, patch) => {
    setParadas((prev) =>
      prev.map((p) => (p._tempId === tempId ? { ...p, ...patch } : p)),
    );
  };

  // Elegir comercio del catálogo: precarga nombre + (si hay 1 dirección) la
  // dirección directa; si hay varias, deja el selector vacío hasta que elija.
  const handleSelectClient = (tempId, clientId) => {
    const client = clientsById.get(clientId);
    if (!client) {
      updateParada(tempId, { clientId: null, selectedAddress: '', comercioNombre: '', direccion: '', comuna: '' });
      return;
    }
    const addrs = clientAddresses(client);
    const only = addrs.length === 1 ? addrs[0] : null;
    updateParada(tempId, {
      clientId,
      comercioNombre: clientDisplayName(client),
      selectedAddress: only?._id ?? '',
      direccion: only?.address ?? '',
      comuna: only?.municipality ?? only?.state ?? '',
    });
  };

  // Elegir una dirección concreta (cliente multi-dirección).
  const handleSelectAddress = (tempId, clientId, addressId) => {
    const client = clientsById.get(clientId);
    const addr = clientAddresses(client).find((a) => a._id === addressId);
    updateParada(tempId, {
      selectedAddress: addressId,
      direccion: addr?.address ?? '',
      comuna: addr?.municipality ?? addr?.state ?? '',
    });
  };

  const addParada = () => {
    const nueva = emptyParada();
    setParadas((prev) => [...prev, nueva]);
    setFocusId(nueva._tempId);
    setExpandedId(null);
  };
  const removeParada = (tempId) =>
    setParadas((prev) => prev.filter((p) => p._tempId !== tempId));

  // ── Drag & drop nativo para reordenar paradas ──
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    setParadas((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requiere setData para iniciar el drag.
    try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
  };
  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };
  const handleDrop = (index) => (e) => {
    e.preventDefault();
    reorder(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };
  // Reordenar por teclado (accesible): mueve la parada arriba/abajo.
  const moveParada = (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= paradas.length) return;
    reorder(index, to);
  };

  const handleSave = async () => {
    if (!nombre.trim()) {
      toast.warn(t('Ingresa un nombre para la ruta'));
      return;
    }
    // El repartidor es OBLIGATORIO: sin `deliveryMan` la ruta no le aparece a
    // ningún chofer en /routes/me (mismo requisito que el flujo de Retiros).
    if (!conductorId) {
      toast.warn(t('Asigna un repartidor: sin chofer la ruta no le aparece a nadie.'));
      return;
    }
    if (!modalidad) {
      toast.warn(t('Elige la modalidad de entrega.'));
      return;
    }
    if (paradas.some((p) => !p.comercioNombre.trim())) {
      toast.warn(t('Todas las paradas deben tener un comercio seleccionado'));
      return;
    }
    // Una parada de un comercio con varias direcciones debe tener la dirección de
    // retiro elegida. Sin esto se enviaba `selectedAddress: undefined` y el back
    // persistía una parada sin `shippingAddress`, corrompiendo la ruta y rompiendo
    // "Rutas con detalle" del operador.
    const sinDireccion = paradas.filter((p) => p.clientId && !p.selectedAddress);
    if (sinDireccion.length > 0) {
      toast.warn(
        t('Selecciona la dirección de retiro de {{n}} (toda parada con varias direcciones la requiere).', {
          n: sinDireccion[0].comercioNombre || t('un comercio'),
        }),
      );
      return;
    }
    if (recurrenciaTipo === 'semanal' && diasSemana.length === 0) {
      toast.warn(t('Selecciona al menos un día para la recurrencia semanal'));
      return;
    }

    setSaving(true);
    try {
      // Hub destino: instalación física (HUB/Bodega) de la PROPIA empresa donde se
      // hace cross-docking; su id es un operationsAddresses[]._id. Solo aplica a la
      // modalidad "hub" (Pick & Go va directo a ruta, sin hub).
      const hub = hubs.find((h) => h.id === hubDestinoId);
      const destinationHubId =
        modalidad === 'hub' ? (hub?.id || hubDestinoId || undefined) : undefined;

      // Recurrencia canónica {type, daysOfWeek}: fuente de verdad del día que matchea
      // la app del chofer. Se envía SIEMPRE (create y edit) junto al `frequency` legacy.
      const recurrence = buildBackendRecurrence(recurrenciaTipo, diasSemana);

      // Todo el detalle operativo por parada viaja DENTRO de clients[] — es el
      // contrato que el back persiste (ventana, bultos, peso, cobro/pago, umbral,
      // geofencing on/off + radio, notas). El orden del array = orden de retiro
      // (drag & drop); Mongoose preserva el orden del array.
      const clientsPayload = paradas.filter((p) => p.clientId).map(buildClientFromParada);

      const payload = {
        routeName: nombre.trim(),
        frequency: recurrenciaToDayType(recurrenciaTipo, diasSemana),
        recurrence,
        deliveryMan: conductorId,
        deliveryMode: modalidad,
        destinationHubId,
        notes: notas?.trim() || undefined,
        clients: clientsPayload,
      };

      if (isEdit && editandoRuta) {
        // Bloqueá guardar si hay paradas sin comercio del catálogo. Antes se
        // descartaban en silencio (filter(p.clientId)) y se avisaba DESPUÉS con un
        // toast.info engañoso; ahora abortamos para que el usuario las resuelva.
        if (paradas.some((p) => !p.clientId)) {
          toast.error(t('Hay paradas sin comercio del catálogo. Resolvelas o quitalas antes de guardar.'));
          return;
        }

        // Detectar eliminaciones: comparamos el conjunto original (snapshot al abrir)
        // contra el conjunto actual a enviar. Identidad = clientId_selectedAddress.
        const currentKeys = new Set(
          paradas
            .filter((p) => p.clientId)
            .map((p) => `${String(p.clientId)}_${String(p.selectedAddress ?? '')}`),
        );
        const removed = [...originalStopKeysRef.current].filter((k) => !currentKeys.has(k));

        // Confirmación explícita si el usuario eliminó paradas. Solo entonces mandamos
        // confirmRemoval:true (evita el 409 route_stops_removal_not_confirmed del back).
        if (removed.length > 0) {
          const confirmado = window.confirm(
            t('Vas a eliminar {{n}} paradas de la ruta. ¿Continuar?', { n: removed.length }),
          );
          if (!confirmado) return;
          payload.confirmRemoval = true;
        }

        const result = await updateRoute(editandoRuta.id, payload);
        // Presence, not truthiness: a failed response with an empty body arrives
        // as `error: ''` (axios keeps it as-is) and must not read as a save.
        if (result && result.error !== undefined) {
          // updateRoute resolves `{ error }` instead of rejecting: rethrow it
          // marked with `apiError` to show backend text, never the raw one.
          const error = new Error(routeErrorText(result.error, t));
          error.apiError = result.error;
          throw error;
        }
        toast.success(t('Ruta actualizada.'));
      } else {
        const result = await addRouteApi(payload);
        if (result && result.error !== undefined) {
          const error = new Error(routeErrorText(result.error, t));
          error.apiError = result.error;
          throw error;
        }
        toast.success(t('Ruta de recolección creada.'));
      }

      onSaved?.();
      onClose();
    } catch (e) {
      // Only an error marked with `apiError` carries backend text; a throw from
      // our own code (TypeError, etc.) falls back to the generic message.
      if (e?.apiError === undefined) console.error('Error al guardar la ruta:', e);
      toast.error(e?.apiError !== undefined ? e.message : t('Error al guardar la ruta'));
    } finally {
      setSaving(false);
    }
  };


  const hubOptions = hubs.map((h) => ({ value: h.id, label: h.nombre }));
  // Conductores ordenados A-Z por nombre para el searchable select.
  const conductorOptions = [...drivers]
    .map((d) => ({
      value: d._id ?? d.id ?? '',
      label: [d.name, d.lastName].filter(Boolean).join(' ') || d.name || t('Sin nombre'),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base', numeric: true }));
  // Comercios disponibles para elegir en cada parada. La opción conserva razón social y
  // RUT (además del nombre) para que el filtro y la 2ª línea del menú los usen.
  const clientOptions = useMemo(
    () =>
      clients
        .filter((c) => clientAddresses(c).length > 0)
        .map((c) =>
          toCommerceOption(
            { ...c, _id: c._id ?? c.id, name: clientDisplayName(c) },
            t('Sin nombre'),
          ),
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clients, t],
  );

  // Totales en vivo (feedback en la barra de paradas): cobro al cliente, pago al
  // repartidor y margen estimado de la ruta.
  const totals = useMemo(() => {
    const cobro = paradas.reduce((s, p) => s + (Number(p.valorCobrarCliente) || 0), 0);
    const pago = paradas.reduce((s, p) => s + (Number(p.valorPagarRepartidor) || 0), 0);
    return { cobro, pago, margen: cobro - pago };
  }, [paradas]);

  // Checklist en vivo: refleja, en el mismo orden, las validaciones de handleSave
  // para que el operador vea qué falta antes de guardar (los toasts se mantienen).
  const checklist = useMemo(() => {
    // En edición handleSave además exige comercio del catálogo (clientId): una
    // parada heredada puede tener nombre pero no clientId.
    const sinComercio = paradas.filter((p) => !p.comercioNombre.trim() || (isEdit && !p.clientId));
    const sinDireccion = paradas.filter((p) => p.clientId && !p.selectedAddress);
    return [
      { key: 'nombre', ok: Boolean(nombre.trim()), label: t('Nombre de la ruta'), target: 'prog-nombre' },
      { key: 'chofer', ok: Boolean(conductorId), label: t('Chofer asignado'), target: 'prog-chofer' },
      { key: 'modalidad', ok: Boolean(modalidad), label: t('Modalidad de entrega'), target: 'prog-modalidad' },
      {
        key: 'comercio',
        ok: sinComercio.length === 0,
        label: sinComercio.length
          ? t('Paradas sin comercio: {{n}}', { n: sinComercio.length })
          : t('Comercio en cada parada'),
        target: sinComercio[0] ? `prog-comercio-${sinComercio[0]._tempId}` : null,
      },
      {
        key: 'direccion',
        ok: sinDireccion.length === 0,
        label: sinDireccion.length
          ? t('Paradas sin dirección de retiro: {{n}}', { n: sinDireccion.length })
          : t('Dirección de retiro en cada parada'),
        // El selector de dirección solo existe para comercios multi-dirección; si no
        // está (p. ej. parada heredada sin dirección), se lleva al comercio.
        target: sinDireccion[0]
          ? [`prog-dir-${sinDireccion[0]._tempId}`, `prog-comercio-${sinDireccion[0]._tempId}`]
          : null,
      },
      {
        key: 'dias',
        ok: !(recurrenciaTipo === 'semanal' && diasSemana.length === 0),
        label: t('Al menos un día de recurrencia'),
        target: 'prog-dias',
      },
    ];
  }, [paradas, nombre, conductorId, modalidad, recurrenciaTipo, diasSemana, isEdit, t]);
  const pendientes = checklist.filter((c) => !c.ok);

  const focusTarget = (target) => {
    const ids = Array.isArray(target) ? target : [target];
    const el = ids.map((id) => id && document.getElementById(id)).find(Boolean);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus({ preventScroll: true });
  };

  const modalidadActiva = MODALIDAD_OPTS.find((o) => o.value === modalidad);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="full"
      bodyClassName="overflow-y-auto lg:overflow-hidden"
      title={isEdit ? t('Editar ruta: {{nombre}}', { nombre: editandoRuta?.nombre }) : t('Programar nueva ruta')}
      description={
        isEdit
          ? t('Ajusta el chofer, la recurrencia y las paradas. Arrastra las paradas para cambiar el orden de retiro.')
          : t('Define la plantilla recurrente. Cada día genera automáticamente una instancia asignable.')
      }
      headerActions={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t('Cancelar')}
          </Button>
          <Button size="sm" onClick={handleSave} loading={saving}>
            {isEdit ? t('Guardar cambios') : t('Crear ruta')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 lg:h-full lg:grid-cols-[22.5rem_minmax(0,1fr)] anim-fade-in-up">
        {/* ── Columna izquierda: configuración de la ruta ───────────────── */}
        {/* OJO: gap-5/py-5 (y 3-4) los pisa el Bootstrap global con !important
            (3rem); gap-6/py-6 no colisionan y dan el ritmo real. */}
        <aside
          aria-label={t('Ruta')}
          className="flex flex-col gap-6 px-6 py-6 border-b border-border lg:border-b-0 lg:border-r lg:overflow-y-auto bg-bg-subtle/40"
        >

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="prog-nombre">{t('Nombre de la ruta')}</FieldLabel>
            <input
              id="prog-nombre"
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={t('Ej: Santiago Centro - Zona 1')}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="prog-chofer">{t('Chofer')}</FieldLabel>
            <SearchableSelect
              id="prog-chofer"
              options={conductorOptions}
              value={conductorId}
              onChange={(v) => setConductorId(v)}
              placeholder={t('Buscar repartidor...')}
              noOptionsText={t('Sin resultados')}
              hint={conductorId ? undefined : t('Sin chofer la ruta no le aparece a nadie en la app.')}
              isClearable
            />
          </div>

          {/* Modalidad: control segmentado + explicación de la opción elegida. */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel id="prog-modalidad-label">{t('Modalidad')}</FieldLabel>
            <div
              role="group"
              aria-labelledby="prog-modalidad-label"
              className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-bg-muted p-1"
            >
              {MODALIDAD_OPTS.map((opt, i) => {
                const active = modalidad === opt.value;
                return (
                  <button
                    key={opt.value}
                    id={i === 0 ? 'prog-modalidad' : undefined}
                    type="button"
                    onClick={() => setModalidad(opt.value)}
                    aria-pressed={active}
                    className={cn(
                      'h-9 rounded-md px-2 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                      active ? 'bg-bg text-brand-700 shadow-soft' : 'text-fg-muted hover:text-fg',
                    )}
                  >
                    {t(opt.label)}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-fg-muted">
              {modalidadActiva ? t(modalidadActiva.hint) : t('Elige cómo sale lo retirado.')}
            </p>
            {/* Hub destino: solo con "A hub destino" (Pick & Go no pasa por hub).
                Si solo hay 1 hub queda preseleccionado (ver efecto arriba). */}
            {modalidad === 'hub' && (
              <div className="mt-1.5">
                <Select
                  aria-label={t('Hub destino')}
                  options={hubOptions}
                  value={hubDestinoId}
                  onChange={(e) => setHubDestinoId(e.target.value)}
                  placeholder={t('Selecciona hub...')}
                />
              </div>
            )}
          </div>

          {/* Recurrencia: presets + chips de día siempre visibles. */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel id="prog-recurrencia-label">{t('Recurrencia')}</FieldLabel>
            <div
              role="group"
              aria-labelledby="prog-recurrencia-label"
              className="flex gap-1 rounded-lg border border-border bg-bg-muted p-1"
            >
              {RECURRENCIA_OPTS.map((opt) => {
                const active = recurrenciaTipo === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => selectRecurrencia(opt.value)}
                    aria-pressed={active}
                    className={cn(
                      'h-9 flex-auto whitespace-nowrap rounded-md px-2 text-xs font-medium transition-colors duration-150 motion-reduce:transition-none',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                      active ? 'bg-bg text-brand-700 shadow-soft' : 'text-fg-muted hover:text-fg',
                    )}
                  >
                    {t(opt.label)}
                  </button>
                );
              })}
            </div>
            {recurrenciaTipo === 'puntual' ? (
              <input
                type="date"
                value={fechaPuntual}
                onChange={(e) => setFechaPuntual(e.target.value)}
                aria-label={t('Fecha')}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            ) : (
              <div className="grid grid-cols-7 gap-1 mt-1" role="group" aria-label={t('Días de la semana')}>
                {DIAS_SEMANA.map((d, i) => {
                  const on = diasActivos.includes(d.value);
                  return (
                    <button
                      key={d.value}
                      id={i === 0 ? 'prog-dias' : undefined}
                      type="button"
                      onClick={() => toggleDia(d.value)}
                      aria-pressed={on}
                      className={cn(
                        'h-10 rounded-md border text-xs font-semibold transition-colors duration-150 motion-reduce:transition-none',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1',
                        on
                          ? 'border-brand-500 bg-brand-500 text-white'
                          : 'border-border bg-bg text-fg-muted hover:border-brand-300 hover:text-fg',
                      )}
                    >
                      {t(d.label)}
                    </button>
                  );
                })}
              </div>
            )}
            {recurrenciaTipo === 'semanal' && diasSemana.length === 0 && (
              <p className="text-xs text-status-error">{t('Elige al menos un día.')}</p>
            )}
          </div>

          {/* Notas: secundarias, colapsadas hasta que se pidan o existan. */}
          {notasOpen ? (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="prog-notas">{t('Notas de la ruta')}</FieldLabel>
              <textarea
                id="prog-notas"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={3}
                autoFocus={!notas}
                className="w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                placeholder={t('Observaciones generales de la ruta...')}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setNotasOpen(true)}
              className="self-start inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <StickyNote className="w-4 h-4" aria-hidden />
              {t('Agregar notas de la ruta')}
            </button>
          )}

          {/* Checklist: qué falta para poder guardar; cada pendiente lleva al campo. */}
          <div className="mt-auto border-t border-border pt-[1.125rem]">
            {pendientes.length === 0 ? (
              <p aria-live="polite" className="flex items-center gap-2 text-sm font-semibold text-fg">
                <CircleCheck className="w-4 h-4 shrink-0 text-status-success" aria-hidden />
                {isEdit ? t('Listo para guardar') : t('Listo para crear')}
              </p>
            ) : (
              <p aria-live="polite" className="text-sm font-semibold text-fg mb-2">
                {t('Falta completar: {{n}}', { n: pendientes.length })}
              </p>
            )}
            <ul className="space-y-1">
              {pendientes.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => focusTarget(item.target)}
                    className="flex w-full items-center gap-2 rounded py-0.5 text-left text-xs font-medium text-fg hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <CircleAlert className="w-3.5 h-3.5 shrink-0 text-status-warning" aria-hidden />
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* ── Columna derecha: paradas ──────────────────────────────────── */}
        <section className="flex flex-col lg:min-h-0 lg:overflow-y-auto">
          {/* Cabecera sticky: primer hijo del contenedor que scrollea, sin padding
              encima, para que nada asome por arriba al hacer scroll. */}
          <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border bg-bg px-6 py-3">
            <div className="flex items-baseline gap-2">
              <h3 className="text-base font-semibold text-fg">{t('Paradas')}</h3>
              <span className="text-sm text-fg-muted tabular-nums">{paradas.length}</span>
            </div>
            {(totals.cobro > 0 || totals.pago > 0) && (
              <dl className="flex items-center gap-4 text-xs tabular-nums">
                <div className="flex gap-1">
                  <dt className="text-fg-muted">{t('Cobro')}</dt>
                  <dd className="font-semibold text-fg">{formatCLP(totals.cobro)}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="text-fg-muted">{t('Pago')}</dt>
                  <dd className="font-semibold text-fg">{formatCLP(totals.pago)}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="text-fg-muted">{t('Margen')}</dt>
                  <dd className={cn('font-semibold', totals.margen < 0 ? 'text-status-error' : 'text-status-success')}>
                    {formatCLP(totals.margen)}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          <div className="px-6 py-4">
            <p className="text-xs text-fg-muted mb-3">
              {t('Elige el comercio y su dirección se completa sola. Arrastra para reordenar el retiro.')}
            </p>

            {showSkeleton ? (
              <ParadasSkeleton />
            ) : paradas.length === 0 ? (
              <EmptyState
                icon={<Store />}
                title={t('Sin paradas')}
                description={t('Agrega comercios del catálogo como paradas de esta ruta.')}
              />
            ) : (
              <ol className="rounded-xl border border-border bg-bg divide-y divide-border">
                {paradas.map((p, idx) => (
                  <ParadaRow
                    key={p._tempId}
                    t={t}
                    index={idx}
                    total={paradas.length}
                    parada={p}
                    client={clientsById.get(p.clientId)}
                    clientOptions={clientOptions}
                    autoFocus={focusId === p._tempId}
                    onAutoFocusDone={() => setFocusId(null)}
                    expanded={expandedId === p._tempId}
                    onToggleExpanded={() => setExpandedId((cur) => (cur === p._tempId ? null : p._tempId))}
                    isDragging={dragIndex === idx}
                    isDragOver={dragOverIndex === idx && dragIndex !== idx}
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver(idx)}
                    onDrop={handleDrop(idx)}
                    onDragEnd={handleDragEnd}
                    onMove={(delta) => moveParada(idx, delta)}
                    onSelectClient={(cid) => handleSelectClient(p._tempId, cid)}
                    onSelectAddress={(aid) => handleSelectAddress(p._tempId, p.clientId, aid)}
                    onUpdate={(patch) => updateParada(p._tempId, patch)}
                    onRemove={() => removeParada(p._tempId)}
                    canRemove={paradas.length > 1}
                  />
                ))}
              </ol>
            )}

            <button
              type="button"
              onClick={addParada}
              disabled={showSkeleton}
              className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border text-sm font-medium text-fg-muted transition-colors duration-150 hover:border-brand-300 hover:bg-brand-50/40 hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transition-none"
            >
              <Plus className="w-4 h-4" aria-hidden />
              {t('Agregar parada')}
            </button>

            {clients.length === 0 && !showSkeleton && (
              <div className="mt-3">
                <PendingBackendNotice icon={<Store className="w-3.5 h-3.5 shrink-0" />}>
                  {t('No hay comercios activos con dirección para asignar a la ruta.')}
                </PendingBackendNotice>
              </div>
            )}
          </div>
        </section>
      </div>
    </Drawer>
  );
}

// ── Una parada: fila compacta arrastrable; detalles operativos bajo demanda ──
// ≥xl todo va en una línea (comercio · dirección · ventana · acciones); por debajo,
// dirección y ventana bajan a una segunda línea (wrapper `xl:contents`).
function ParadaRow({
  t, index, total, parada: p, client, clientOptions, autoFocus, onAutoFocusDone, expanded, onToggleExpanded,
  isDragging, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd, onMove,
  onSelectClient, onSelectAddress, onUpdate, onRemove, canRemove,
}) {
  const addrs = clientAddresses(client);
  const hasMultiple = addrs.length > 1;
  const faltaDireccion = Boolean(p.clientId) && !p.selectedAddress;
  const addressOptions = addrs.map((a) => ({
    value: a._id,
    label: `${a.address}${a.municipality ? `, ${a.municipality}` : ''}${a.type ? ` (${a.type})` : ''}`,
  }));
  const numberFields = [
    { key: 'bultosEsperados', label: t('Bultos esperados'), placeholder: '0' },
    { key: 'pesoEsperadoKg', label: t('Peso esperado (kg)'), placeholder: '0', step: '0.1' },
    {
      key: 'umbralCobroPedidos',
      // El campo es `freeOrdersThreshold`: el número de pedidos A PARTIR DEL CUAL el
      // retiro deja de cobrarse. El label y el tooltip anteriores ("Umbral de cobro" /
      // "sobre este número de pedidos se genera el cobro") decían exactamente lo
      // contrario, en los cinco idiomas.
      //
      // Mientras la regla no se evaluaba, el error era invisible. Desde que el informe de
      // colectas la aplica, un operador que cargue el umbral al revés cobra de más o de
      // menos, así que el texto tiene que decir el sentido correcto sin margen de lectura.
      label: t('Pedidos para retiro gratis'),
      placeholder: '0',
      title: t('Desde este número de pedidos, el retiro es gratis para el comercio'),
    },
    { key: 'valorCobrarCliente', label: t('Cobrar cliente (CLP)'), placeholder: '0' },
    { key: 'valorPagarRepartidor', label: t('Pagar repartidor (CLP)'), placeholder: '0' },
  ];

  // Resumen compacto de los valores operativos (visible cuando está colapsado).
  const resumen = [];
  if (Number(p.bultosEsperados) > 0) resumen.push(`${p.bultosEsperados} ${t('bultos')}`);
  if (Number(p.pesoEsperadoKg) > 0) resumen.push(`${p.pesoEsperadoKg} kg`);
  if (Number(p.valorCobrarCliente) > 0) resumen.push(formatCLP(Number(p.valorCobrarCliente)));
  if (!p.geofencingActivo) resumen.push(t('sin geofencing'));

  const detailsId = `prog-detalles-${p._tempId}`;
  const iconBtn =
    'inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent';
  const timeInput =
    'h-10 min-w-0 flex-1 sm:w-32 sm:flex-none rounded-lg border border-border bg-bg px-2 text-sm tabular-nums text-fg dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <li
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        // OJO: no aplicar `transform` en la fila que se arrastra — mutar el transform
        // del elemento durante el drag cancela el DnD nativo en Chromium.
        'transition-[background-color,opacity,box-shadow] duration-150 motion-reduce:transition-none first:rounded-t-xl last:rounded-b-xl',
        isDragging && 'opacity-50',
        isDragOver && 'bg-brand-50/60 shadow-[inset_0_2px_0_0_rgb(var(--brand-500))]',
        expanded && !isDragOver && 'bg-bg-subtle/60',
      )}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,1.15fr)_auto_auto] items-start gap-x-3 gap-y-2 px-3 py-2.5">
        {/* Asa + número de orden */}
        <div className="flex h-10 items-center gap-1">
          <button
            type="button"
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            className="cursor-grab active:cursor-grabbing text-fg-subtle hover:text-fg p-0.5 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 touch-none"
            aria-label={t('Arrastrar para reordenar')}
            title={t('Arrastrar para reordenar')}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-700 text-xs font-bold flex items-center justify-center tabular-nums">
            {index + 1}
          </span>
        </div>

        {/* Comercio */}
        <SearchableSelect
          id={`prog-comercio-${p._tempId}`}
          aria-label={t('Comercio de la parada {{n}}', { n: index + 1 })}
          options={clientOptions}
          value={p.clientId ?? ''}
          onChange={(v) => onSelectClient(v)}
          placeholder={t('Buscar comercio...')}
          noOptionsText={t('Sin resultados')}
          filterOption={filterCommerceOption}
          formatOptionLabel={formatCommerceOptionLabel}
          autoFocus={autoFocus}
          openMenuOnFocus={autoFocus}
          onMenuClose={autoFocus ? onAutoFocusDone : undefined}
        />

        {/* Dirección + ventana: 2ª línea bajo xl, columnas propias en xl */}
        <div className="col-start-2 col-end-[-1] row-start-2 flex flex-wrap items-start gap-2 xl:contents">
          <div className="min-w-0 flex-1 basis-60 xl:col-start-3 xl:row-start-1">
            {p.clientId ? (
              hasMultiple ? (
                <Select
                  id={`prog-dir-${p._tempId}`}
                  aria-label={t('Dirección de retiro')}
                  options={addressOptions}
                  value={p.selectedAddress || ''}
                  onChange={(e) => onSelectAddress(e.target.value)}
                  placeholder={t('Elige la dirección de retiro...')}
                  error={faltaDireccion ? t('Este comercio tiene varias direcciones: elige una.') : undefined}
                />
              ) : (
                <div
                  className="flex h-10 items-center gap-2 rounded-lg px-1 text-sm text-fg"
                  title={[p.direccion, p.comuna].filter(Boolean).join(', ')}
                >
                  <MapPin className="w-4 h-4 text-brand-600 shrink-0" aria-hidden />
                  <span className="min-w-0 truncate">
                    {p.direccion || t('Sin dirección registrada')}
                    {p.comuna ? <span className="text-fg-muted">, {p.comuna}</span> : null}
                  </span>
                </div>
              )
            ) : (
              <div className="flex h-10 items-center gap-2 px-1 text-sm text-fg-subtle">
                <MapPin className="w-4 h-4 shrink-0" aria-hidden />
                <span className="truncate">{t('Elige un comercio para ver su dirección.')}</span>
              </div>
            )}
          </div>

          <div
            className="flex h-10 w-full items-center gap-1.5 sm:w-auto sm:shrink-0 xl:col-start-4 xl:row-start-1"
            role="group"
            aria-label={t('Ventana de retiro')}
          >
            <input
              type="time"
              value={p.ventanaDesde}
              onChange={(e) => onUpdate({ ventanaDesde: e.target.value })}
              aria-label={t('Ventana desde')}
              className={timeInput}
            />
            <span className="text-fg-subtle" aria-hidden>–</span>
            <input
              type="time"
              value={p.ventanaHasta}
              onChange={(e) => onUpdate({ ventanaHasta: e.target.value })}
              aria-label={t('Ventana hasta')}
              className={timeInput}
            />
          </div>
        </div>

        {/* Acciones: detalles · subir/bajar · eliminar */}
        <div className="col-start-3 row-start-1 flex h-10 items-center gap-0.5 xl:col-start-5">
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            aria-controls={expanded ? detailsId : undefined}
            aria-label={t('Detalles operativos')}
            title={t('Detalles operativos')}
            className={cn(iconBtn, 'h-8 w-8', expanded && 'text-fg bg-bg-muted')}
          >
            <ChevronDown
              className={cn('w-4 h-4 transition-transform duration-200 motion-reduce:transition-none', expanded && 'rotate-180')}
            />
          </button>
          <div className="flex flex-col">
            <button
              type="button"
              onClick={() => onMove(-1)}
              disabled={index === 0}
              className={cn(iconBtn, 'h-4 w-6')}
              aria-label={t('Subir parada')}
              title={t('Subir')}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onMove(1)}
              disabled={index === total - 1}
              className={cn(iconBtn, 'h-4 w-6')}
              aria-label={t('Bajar parada')}
              title={t('Bajar')}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <button
            type="button"
            onClick={onRemove}
            disabled={!canRemove}
            className={cn(iconBtn, 'h-8 w-8 hover:text-status-error hover:bg-status-error-soft')}
            aria-label={t('Eliminar parada')}
            title={t('Eliminar parada')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Resumen de detalles (colapsado) */}
        {!expanded && resumen.length > 0 && (
          <button
            type="button"
            onClick={onToggleExpanded}
            className="col-start-2 col-end-[-1] -mt-1 justify-self-start rounded text-left text-xs text-fg-muted hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {resumen.join(' · ')}
          </button>
        )}

        {/* Detalles operativos — divulgación progresiva (una parada a la vez) */}
        {expanded && (
          <div id={detailsId} className="col-start-2 col-end-[-1] anim-fade-in-up">
              <div className="pt-1 pb-1 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
                  {numberFields.map((f) => (
                    <div key={f.key} title={f.title} className="flex flex-col gap-1">
                      <FieldLabel htmlFor={`${f.key}-${p._tempId}`}>{f.label}</FieldLabel>
                      <input
                        id={`${f.key}-${p._tempId}`}
                        type="number"
                        value={p[f.key]}
                        onChange={(e) => onUpdate({ [f.key]: e.target.value })}
                        className="h-9 w-full rounded-lg border border-border bg-bg px-3 text-sm tabular-nums text-fg focus:outline-none focus:ring-2 focus:ring-brand-500"
                        placeholder={f.placeholder}
                        min="0"
                        step={f.step}
                      />
                    </div>
                  ))}
                </div>
                {/* Geofencing: on/off (del back) + radio en metros */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <Switch
                    checked={!!p.geofencingActivo}
                    onChange={(next) => onUpdate({ geofencingActivo: next })}
                    label={t('Geofencing')}
                    size="sm"
                  />
                  <div className="flex items-center gap-2">
                    <label htmlFor={`geo-${p._tempId}`} className="text-xs text-fg-muted whitespace-nowrap">
                      {t('Radio (m)')}
                    </label>
                    <input
                      id={`geo-${p._tempId}`}
                      type="number"
                      value={p.geofencingMetros}
                      onChange={(e) => onUpdate({ geofencingMetros: e.target.value })}
                      disabled={!p.geofencingActivo}
                      className="h-9 w-24 rounded-lg border border-border bg-bg px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="100"
                      min="0"
                    />
                  </div>
                </div>
              </div>
          </div>
        )}
      </div>
    </li>
  );
}

// Skeleton con la forma de las filas de parada — evita el "salto" mientras llega el catálogo.
function ParadasSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-bg divide-y divide-border">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="w-6 h-6 rounded-full" />
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-4 flex-1 hidden xl:block" />
          <Skeleton className="h-10 w-52" />
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
}
